import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { getAllExams } from '../../services/examService';
import Icon from '../icons/Icon';
import { Exam } from '../../types';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.75);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
`;

const Modal = styled.div`
  background: white;
  border-radius: 20px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.35);
  overflow: hidden;

  @media (max-width: 768px) {
    max-height: 95vh;
    border-radius: 16px;
  }
`;

const ModalHeader = styled.div`
  padding: 24px 32px 20px;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  gap: 12px;

  @media (max-width: 768px) {
    padding: 20px 20px 16px;
  }
`;

const ModalHeaderText = styled.div`
  flex: 1;
`;

const CloseButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 2px solid #e2e8f0;
  background: white;
  color: #64748b;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s ease;
  line-height: 1;

  &:hover {
    background: #f1f5f9;
    border-color: #cbd5e1;
    color: #1e293b;
  }
`;

const ModalBody = styled.div`
  overflow-y: auto;
  flex: 1;
  padding: 24px 32px;

  @media (max-width: 768px) {
    padding: 16px 20px;
  }

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: #f8fafc;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
`;

const ModalFooter = styled.div`
  padding: 20px 32px;
  border-top: 1px solid #f1f5f9;
  flex-shrink: 0;
  background: #fafbfc;

  @media (max-width: 768px) {
    padding: 16px 20px;
  }
`;

const Title = styled.h2`
  font-size: 26px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 6px 0;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

const Subtitle = styled.p`
  font-size: 15px;
  color: #64748b;
  margin: 0;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const ExamList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ExamCard = styled.div<{ selected?: boolean }>`
  background: ${props => props.selected
    ? 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)'
    : '#ffffff'
  };
  border: 2px solid ${props => props.selected ? '#3b82f6' : '#e2e8f0'};
  border-radius: 14px;
  padding: 18px 20px;
  cursor: pointer;
  transition: all 0.25s ease;
  display: flex;
  align-items: center;
  gap: 16px;
  position: relative;

  ${props => props.selected && `
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
  `}

  &:hover {
    border-color: #93c5fd;
    background: ${props => props.selected ? 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)' : '#f8fafc'};
    box-shadow: 0 4px 16px rgba(59, 130, 246, 0.1);
  }

  @media (max-width: 768px) {
    padding: 14px 16px;
    gap: 12px;
  }
`;

const ExamCardIcon = styled.div<{ type: string }>`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: ${props => props.type === 'buraxilis'
    ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
    : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
  };
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 20px;

  @media (max-width: 768px) {
    width: 38px;
    height: 38px;
    font-size: 18px;
  }
`;

const ExamCardContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const ExamCardCheck = styled.div<{ checked: boolean }>`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid ${props => props.checked ? '#3b82f6' : '#cbd5e1'};
  background: ${props => props.checked ? '#3b82f6' : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s ease;
  color: white;
  font-size: 12px;
`;

const ExamName = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 4px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ExamMeta = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
`;

const ExamTypeBadge = styled.span<{ type: string }>`
  background: ${props => props.type === 'buraxilis'
    ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
    : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
  };
  color: white;
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
`;

const ExamCollectionTag = styled.span`
  color: #94a3b8;
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ConfirmButton = styled.button`
  width: 100%;
  padding: 14px 24px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s ease;
  border: none;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  box-shadow: 0 4px 14px rgba(59, 130, 246, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(59, 130, 246, 0.45);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    box-shadow: none;
    background: #94a3b8;
  }

  @media (max-width: 768px) {
    padding: 13px 20px;
    font-size: 14px;
  }
`;

const SelectedInfo = styled.div`
  font-size: 13px;
  color: #64748b;
  text-align: center;
  margin-bottom: 10px;

  span {
    font-weight: 600;
    color: #3b82f6;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 20px;
  gap: 16px;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid #e2e8f0;
  border-top: 3px solid #3b82f6;
  border-radius: 50%;
  animation: spin 0.9s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background: #fff1f2;
  border: 1px solid #fca5a5;
  color: #dc2626;
  padding: 14px 16px;
  border-radius: 10px;
  text-align: center;
  font-size: 14px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 40px 20px;
  color: #94a3b8;
  text-align: center;
  font-size: 14px;
`;

interface ExamSelectorProps {
  onExamSelect: (exam: Exam) => void;
  currentExam?: Exam | null;
  onClose?: () => void;
}

const ExamSelector: React.FC<ExamSelectorProps> = ({ onExamSelect, currentExam, onClose }) => {
  const [exams, setExams] = useState<Exam[]>([]);
  const [selectedExam, setSelectedExam] = useState<Exam | null>(currentExam ?? null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadExams();
  }, []);

  const loadExams = async () => {
    try {
      setLoading(true);
      setError('');
      const examList = await getAllExams();
      setExams(examList);
    } catch (err: any) {
      console.error('❌ ExamSelector: Error loading exams:', err);
      setError('İmtahanlar yüklənərkən xəta baş verdi.');
    } finally {
      setLoading(false);
    }
  };

  const handleExamClick = (exam: Exam) => {
    setSelectedExam(exam);
  };

  const handleConfirm = () => {
    if (selectedExam) {
      onExamSelect(selectedExam);
    }
  };

  const examEmoji = (type: string) => type === 'buraxilis' ? '🎓' : '📋';

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalHeaderText>
            <Title>İmtahan Seçin</Title>
            <Subtitle>İdarə etmək istədiyiniz imtahanı seçin</Subtitle>
          </ModalHeaderText>
          {onClose && (
            <CloseButton onClick={onClose} title="Bağla">
              ✕
            </CloseButton>
          )}
        </ModalHeader>

        <ModalBody>
          {loading && (
            <LoadingContainer>
              <LoadingSpinner />
              <div style={{ color: '#64748b', fontSize: 14 }}>İmtahanlar yüklənir...</div>
            </LoadingContainer>
          )}

          {!loading && error && <ErrorMessage>{error}</ErrorMessage>}

          {!loading && !error && exams.length === 0 && (
            <EmptyState>
              <div style={{ fontSize: 36 }}>📭</div>
              <div>Heç bir imtahan tapılmadı.</div>
            </EmptyState>
          )}

          {!loading && !error && exams.length > 0 && (
            <ExamList>
              {exams.map(exam => (
                <ExamCard
                  key={exam.id}
                  selected={selectedExam?.id === exam.id}
                  onClick={() => handleExamClick(exam)}
                >
                  <ExamCardIcon type={exam.type}>
                    {examEmoji(exam.type)}
                  </ExamCardIcon>
                  <ExamCardContent>
                    <ExamName>{exam.name}</ExamName>
                    <ExamMeta>
                      <ExamTypeBadge type={exam.type}>
                        {exam.type === 'buraxilis' ? 'Buraxılış' : 'Blok'}
                      </ExamTypeBadge>
                      <ExamCollectionTag>
                        <Icon name="archive" size={12} color="#94a3b8" />
                        {exam.collection}
                      </ExamCollectionTag>
                      {currentExam?.id === exam.id && (
                        <span style={{
                          fontSize: 11,
                          color: '#059669',
                          fontWeight: 600,
                          background: '#dcfce7',
                          padding: '1px 8px',
                          borderRadius: 20,
                        }}>
                          Aktiv
                        </span>
                      )}
                    </ExamMeta>
                  </ExamCardContent>
                  <ExamCardCheck checked={selectedExam?.id === exam.id}>
                    {selectedExam?.id === exam.id && '✓'}
                  </ExamCardCheck>
                </ExamCard>
              ))}
            </ExamList>
          )}
        </ModalBody>

        <ModalFooter>
          {selectedExam && (
            <SelectedInfo>
              Seçildi: <span>{selectedExam.name}</span>
            </SelectedInfo>
          )}
          <div style={{ display: 'flex', gap: 10 }}>
            {onClose && (
              <button
                onClick={onClose}
                style={{
                  flex: '0 0 auto',
                  padding: '14px 20px',
                  borderRadius: 12,
                  border: '2px solid #e2e8f0',
                  background: 'white',
                  color: '#64748b',
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Geri
              </button>
            )}
            <ConfirmButton
              disabled={!selectedExam}
              onClick={handleConfirm}
              style={{ flex: 1 }}
            >
              {selectedExam ? 'Davam et →' : 'İmtahan seçin'}
            </ConfirmButton>
          </div>
        </ModalFooter>
      </Modal>
    </Overlay>
  );
};

export default ExamSelector;
